import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { offersService } from '../../../../services/offersService';
import Loading from '../../../shared/ui/Loading';
import { toast } from 'react-hot-toast';

const MyOffers = forwardRef((props, ref) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    count: 0
  });

  const statusOptions = [
    { value: 'all', label: 'Tümü', count: 0 },
    { value: 'pending', label: 'Beklemede', count: 0 },
    { value: 'accepted', label: 'Kabul Edildi', count: 0 },
    { value: 'rejected', label: 'Reddedildi', count: 0 },
    { value: 'expired', label: 'Süresi Doldu', count: 0 }
  ];

  const fetchOffers = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page
      };
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      console.log('Fetching offers with params:', params); // Debug log
      const response = await offersService.getMyOffers(params);
      console.log('Offers response:', response); // Debug log
      
      setOffers(response.results || response || []);
      setPagination({
        page,
        totalPages: Math.ceil((response.count || response.length || 0) / 20),
        count: response.count || response.length || 0
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching offers:', err); // Debug log
      setError('Teklifler yüklenirken bir hata oluştu');
      toast.error('Teklifler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check token on component mount
    offersService.checkToken();
    fetchOffers();
  }, [statusFilter]);

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh: () => fetchOffers(pagination.page)
  }));

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'label-orange';
      case 'accepted':
        return 'label-success';
      case 'rejected':
        return 'label-danger';
      case 'expired':
        return 'label-default';
      default:
        return 'label-default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'accepted':
        return 'Kabul Edildi';
      case 'rejected':
        return 'Reddedildi';
      case 'expired':
        return 'Süresi Doldu';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  if (loading && offers.length === 0) {
    return (
      <div className="text-center p-4">
        <Loading />
      </div>
    );
  }

  return (
    <div>
      {/* Status Filters */}
      <div className="box m-b">
        <div className="box-header">
          <h3>Tekliflerim</h3>
        </div>
        <div className="box-body">
          <div className="nav nav-pills" style={{ flexWrap: 'wrap' }}>
            {statusOptions.map((option) => (
              <li key={option.value} className="nav-item m-b-xs">
                <button
                  className={`nav-link ${statusFilter === option.value ? 'active' : ''}`}
                  onClick={() => setStatusFilter(option.value)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      {error ? (
        <div className="alert alert-danger">
          {error}
        </div>
      ) : (
        <>
          <div className="text-muted m-b">
            Toplam {pagination.count} teklif bulundu
          </div>
          
          {offers.length === 0 ? (
            <div className="box text-center p-4">
              <i className="fa fa-file-text-o fa-3x text-muted m-b"></i>
              <h4>
                {statusFilter === 'all' 
                  ? 'Henüz teklif vermediniz'
                  : `${getStatusText(statusFilter)} durumunda teklif bulunmuyor`
                }
              </h4>
              <p className="text-muted">
                {statusFilter === 'all' 
                  ? 'Açık talepler sekmesinden müzisyen taleplerine teklif verebilirsiniz.'
                  : `Farklı durumdaki tekliflerinizi görmek için filtreleri değiştirebilirsiniz.`
                }
              </p>
            </div>
          ) : (
            <div className="row">
              {offers.map((offer) => (
                <div key={offer.id} className="col-sm-6 col-lg-4 m-b">
                  <div className="box">
                    <div className="box-header">
                      <div className="pull-right">
                        <span className={`label ${getStatusBadgeClass(offer.status)}`}>
                          {getStatusText(offer.status)}
                        </span>
                      </div>
                      <h5 className="m-0" style={{ paddingRight: '80px' }}>
                        {offer.request_details?.organization_type || 'Belirtilmemiş'}
                      </h5>
                      <small className="text-muted">
                        {offer.request_details?.musician_type || 'Belirtilmemiş'}
                      </small>
                    </div>
                    
                    <div className="box-body">
                      <div className="m-b-sm">
                        <i className="fa fa-map-marker text-muted m-r-xs"></i>
                        <small>
                          {offer.request_details?.city || 'Belirtilmemiş'} / {offer.request_details?.town || 'Belirtilmemiş'}
                        </small>
                      </div>
                      
                      {/* Offer Price - Prominently displayed */}
                      <div className="text-center m-b">
                        <h3 className="text-primary m-0">
                          {formatPrice(offer.price)}
                        </h3>
                        <small className="text-muted">Teklif Edilen Fiyat</small>
                      </div>
                      
                      {/* Message */}
                      <div className="m-b-sm">
                        <strong>Mesajım:</strong>
                        <p className="text-sm" style={{ 
                          minHeight: '40px',
                          fontSize: '12px',
                          lineHeight: '1.4'
                        }}>
                          {offer.message && offer.message.length > 80 
                            ? offer.message.substring(0, 80) + '...'
                            : offer.message || 'Mesaj bulunmuyor'
                          }
                        </p>
                      </div>
                      
                      {/* Dates */}
                      <div className="m-b-sm">
                        <small className="text-muted">
                          <i className="fa fa-clock-o m-r-xs"></i>
                          Gönderildi: {formatDate(offer.offered_at)}
                        </small>
                      </div>
                      
                      {offer.responded_at && (
                        <div className="m-b-sm">
                          <small className="text-muted">
                            <i className="fa fa-reply m-r-xs"></i>
                            Yanıtlandı: {formatDate(offer.responded_at)}
                          </small>
                        </div>
                      )}
                    </div>
                    
                    <div className="box-footer">
                      <div className="text-center">
                        {offer.status === 'pending' && (
                          <span className="text-warning">
                            <i className="fa fa-clock-o m-r-xs"></i>
                            Yanıt bekleniyor
                          </span>
                        )}
                        {offer.status === 'accepted' && (
                          <span className="text-success">
                            <i className="fa fa-check-circle m-r-xs"></i>
                            Teklif kabul edildi
                          </span>
                        )}
                        {offer.status === 'rejected' && (
                          <span className="text-danger">
                            <i className="fa fa-times-circle m-r-xs"></i>
                            Teklif reddedildi
                          </span>
                        )}
                        {offer.status === 'expired' && (
                          <span className="text-muted">
                            <i className="fa fa-clock-o m-r-xs"></i>
                            Süre doldu
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="text-center m-t">
              <div className="btn-group">
                <button 
                  className="btn btn-default"
                  disabled={pagination.page === 1}
                  onClick={() => fetchOffers(pagination.page - 1)}
                >
                  <i className="fa fa-chevron-left"></i>
                </button>
                
                <span className="btn btn-default">
                  {pagination.page} / {pagination.totalPages}
                </span>
                
                <button 
                  className="btn btn-default"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => fetchOffers(pagination.page + 1)}
                >
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default MyOffers; 